import React, { FC, useCallback, useContext, useState } from 'react';
import { Card } from 'Shared/components/Card';
import styles from './styles.module.css';
import { FormInput, ConfirmationModal } from 'Shared/components';
import { PolicyCancellationForm } from './PolicyCancellationForm';
import { Button, Modal, SideDrawer } from '@clearcover/atlas';
import { Can } from 'Shared/utils/auth';
import { PolicyContext } from 'Policies/utils';
import { useAuth0 } from 'Auth';
import {
  cancelPolicies,
  reinstatementPolicy,
  customerInitiatedCancellationPolicy,
  carrierInitiatedCancellationPolicy,
  withdrawCancellationPolicy,
} from 'Admin/authPolicies';
import { useLazyApi } from 'Shared/services/Api';
import { useParams } from 'react-router-dom';
import { PolicyReinstatement } from './PolicyReinstatementForm';
import { useModalHashChange } from 'Shared/hooks';
import {
  EffectivePeriodType,
  TermStatusDetailType,
  TermStatusType,
} from '@clearcover/core-app-types';

export type PolicyCancellationProps = {
  policyStatus: TermStatusType;
  termStatusDetailList: Array<TermStatusDetailType>;
  effectivePeriod: EffectivePeriodType;
  isNSA: boolean;
  reinstatementsWithLapseEnabled?: boolean | null;
  renewalDraft?: { id: string };
};

const NonCoreDisplay = () => {
  return (
    <div data-testid="non-core-display-text">
      <p>
        At this time, only Clearcover Agent Advocates can change or cancel a
        policy.
      </p>
      <div className={styles.message}>
        <>
          <h4>Mobile App</h4>
          <p>
            If a customer needs to make a payment, change a payment method or
            file a claim, encourage them to use the Clearcover app.
          </p>
          <h4>Customer Contact</h4>
          <p>
            If a customer wishes to change or cancel their policy, please have
            them call our dedicated customer line at 1 (855) 444-1875.
          </p>
          <h4>Agent and Partner Contact</h4>
          <p>
            If you would like to reach Clearcover, please find the contact
            information in the{' '}
            <a
              href="https://resources.clearcover.com/hc/en-us"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              Agent Hub
            </a>
            .
          </p>
        </>
      </div>
    </div>
  );
};

type statusVariant = 'cancelled' | 'pendingCancellation' | 'default';

const statusMap: Record<
  statusVariant,
  { title: string; message: string; testId: string }
> = {
  cancelled: {
    title: 'Policy is Cancelled',
    message: 'This policy has been cancelled.',
    testId: 'cancelled-text-message',
  },
  pendingCancellation: {
    title: 'Pending Cancellation',
    message: 'This policy is pending cancellation.',
    testId: 'pending-cancellation-message',
  },
  default: {
    title: 'Cancel Policy',
    message:
      'Please note: Only the main policy holder is able to request a policy cancellation.',
    testId: 'default-cancel-policy-message',
  },
};

export const PolicyCancellation: FC<PolicyCancellationProps> = ({
  policyStatus,
  termStatusDetailList,
  effectivePeriod,
  isNSA,
  reinstatementsWithLapseEnabled,
  renewalDraft,
}) => {
  const { systemOfRecord, refetchHandler, resourceEffectiveGrants } =
    useContext(PolicyContext);
  const { id: policyId } = useParams<{
    id: string;
  }>();
  const [{ loading, error }, executeWithdrawCancellationDraft] = useLazyApi({
    url: `/policies/draft/cancellation/withdraw`,
    method: 'POST',
  });
  const [, executeTerminateDraft] = useLazyApi({});
  const { modalHashChange } = useModalHashChange('cancellation');

  const { user: currentUser } = useAuth0();

  const [cancelPolicyDraftId, setCancelPolicyDraftId] = useState<
    string | undefined
  >(undefined);

  const [initiationType, setInitiationType] = useState<
    'customer_requested' | 'clearcover_initiated'
  >('customer_requested');

  const isPendingCancellation =
    termStatusDetailList.includes('pending_cancellation_other') ||
    termStatusDetailList.includes('pending_cancellation_non_payment');
  const isCancelled = policyStatus === 'cancelled';
  const isFutureEffective = termStatusDetailList.includes(
    'future_effective_date'
  );

  const terminateDraft = useCallback(() => {
    if (cancelPolicyDraftId) {
      executeTerminateDraft({
        url: `policies/${policyId}/drafts/${cancelPolicyDraftId}/terminate`,
        method: 'POST',
      });
    }
  }, [cancelPolicyDraftId, executeTerminateDraft, policyId]);

  const status: statusVariant = isCancelled
    ? 'cancelled'
    : isPendingCancellation
      ? 'pendingCancellation'
      : 'default';

  switch (systemOfRecord) {
    case 'clearcover':
      return (
        <Card title={statusMap[status].title} id={'cancel-policy-card'}>
          <div className={styles.container}>
            <p
              className={styles.message}
              data-testid={statusMap[status].testId}
            >
              {statusMap[status].message}
            </p>

            {!isCancelled && (
              <Can
                grants={resourceEffectiveGrants}
                policy={cancelPolicies}
                yes={() => {
                  if (isPendingCancellation) {
                    return (
                      <Can
                        grants={resourceEffectiveGrants}
                        policy={withdrawCancellationPolicy}
                        yes={() => (
                          <Modal
                            onVisibilityChange={modalHashChange}
                            variant="normal"
                            toggle={(openModal) => (
                              <Button
                                data-testid="withdraw-cancellation-button"
                                size={'md'}
                                block
                                variant="secondary"
                                type="button"
                                onClick={openModal}
                              >
                                Withdraw Cancellation
                              </Button>
                            )}
                          >
                            {(close) => (
                              <ConfirmationModal
                                onSubmit={() =>
                                  executeWithdrawCancellationDraft({
                                    data: { policyId },
                                  }).then(() => {
                                    close();
                                    refetchHandler();
                                  })
                                }
                                close={close}
                                error={error?.message}
                                isSubmitting={loading}
                                headerText="Withdraw Cancellation"
                                bodyText={'Withdraw cancellation of policy'}
                              />
                            )}
                          </Modal>
                        )}
                        no={() => <NonCoreDisplay />}
                      />
                    );
                  }

                  return (
                    <SideDrawer
                      closeSideDrawer={terminateDraft}
                      toggle={(open) => (
                        <div data-testid="open-cancel-policy-form-container">
                          {currentUser?.isInternalUser && (
                            <Can
                              grants={resourceEffectiveGrants}
                              policy={customerInitiatedCancellationPolicy}
                              yes={() => (
                                <div className={styles.radioButtonContainer}>
                                  <FormInput
                                    variant="radio"
                                    name="cancellation-date"
                                    className={styles.paymentRadioGroup}
                                    value={'customer_requested'}
                                    label="Customer Requested"
                                    checked={
                                      initiationType === 'customer_requested'
                                    }
                                    onChecked={() => {
                                      setInitiationType('customer_requested');
                                    }}
                                  />
                                </div>
                              )}
                            />
                          )}
                          <Can
                            grants={resourceEffectiveGrants}
                            policy={carrierInitiatedCancellationPolicy}
                            yes={() => (
                              <div className={styles.radioButtonContainer}>
                                <FormInput
                                  variant="radio"
                                  name="cancellation-date"
                                  className={styles.paymentRadioGroup}
                                  value={'clearcover_initiated'}
                                  label="Clearcover Initiated"
                                  checked={
                                    initiationType === 'clearcover_initiated'
                                  }
                                  onChecked={() => {
                                    setInitiationType('clearcover_initiated');
                                  }}
                                />
                              </div>
                            )}
                          />
                          <Button
                            data-testid="open-cancel-policy-form"
                            block
                            variant="danger-secondary"
                            onClick={open}
                          >
                            Cancel Policy
                          </Button>
                        </div>
                      )}
                    >
                      {(close) => (
                        <PolicyCancellationForm
                          setCancelPolicyDraftId={setCancelPolicyDraftId}
                          effectivePeriod={effectivePeriod}
                          closeForm={(cancelDraft = true) =>
                            new Promise<void>((resolve) => {
                              if (cancelPolicyDraftId && cancelDraft)
                                executeTerminateDraft({
                                  url: `policies/${policyId}/drafts/${cancelPolicyDraftId}/terminate`,
                                  method: 'POST',
                                }).then(() => {
                                  close();
                                  resolve();
                                });
                              else {
                                close();
                                resolve();
                              }
                            })
                          }
                          initiationType={initiationType}
                          renewalDraft={renewalDraft}
                          isFutureEffective={isFutureEffective}
                        />
                      )}
                    </SideDrawer>
                  );
                }}
                no={() => <NonCoreDisplay />}
              />
            )}

            {isCancelled && (
              <Can
                grants={resourceEffectiveGrants}
                policy={reinstatementPolicy}
                yes={() => (
                  <SideDrawer
                    closeSideDrawer={terminateDraft}
                    toggle={(open) => (
                      <div
                        data-testid={`open-reinstate-policy-form-container${
                          cancelPolicyDraftId ? `-${cancelPolicyDraftId}` : ''
                        }`}
                      >
                        <Button
                          data-testid="open-reinstate-policy-form"
                          block
                          variant="secondary"
                          onClick={open}
                        >
                          Reinstate Policy
                        </Button>
                      </div>
                    )}
                  >
                    {(close) => (
                      <PolicyReinstatement
                        setCancelPolicyDraftId={setCancelPolicyDraftId}
                        closeDrawer={() => {
                          terminateDraft();
                          close();
                        }}
                        isNSA={isNSA}
                        reinstatementsWithLapseEnabled={
                          reinstatementsWithLapseEnabled
                        }
                      />
                    )}
                  </SideDrawer>
                )}
                no={() => <NonCoreDisplay />}
              />
            )}
          </div>
        </Card>
      );
    case 'one_inc_3':
    case 'one_inc_4':
      return (
        <Card title={statusMap[status].title} id={'cancel-policy-card'}>
          <div className={styles.container}>
            <NonCoreDisplay />
          </div>
        </Card>
      );
    default:
      return (
        <Card title={statusMap[status].title} id={'cancel-policy-card'}>
          <div className={styles.container}>
            <NonCoreDisplay />
          </div>
        </Card>
      );
  }
};
